import { useEffect, useRef, useState } from 'react';

import { usePatchSheetsSettings, useSheetsSettings, useTestSheetsAccess, useWriteSheetsTestRow } from '../../hooks/use-settings';
import { Badge } from '../ui/badge';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Skeleton } from '../ui/skeleton';
import { useToast } from '../ui/toast';

export function SheetsSection() {
  const { data, isLoading } = useSheetsSettings();
  const patch = usePatchSheetsSettings();
  const testAccess = useTestSheetsAccess();
  const testRow = useWriteSheetsTestRow();
  const toast = useToast();
  const fileRef = useRef<HTMLInputElement>(null);

  const [spreadsheet, setSpreadsheet] = useState('');
  const [keyJson, setKeyJson] = useState('');

  useEffect(() => {
    setSpreadsheet(data?.settings.spreadsheet_id ?? '');
  }, [data]);

  function submit(e: React.FormEvent) {
    e.preventDefault();
    patch.mutate(
      {
        spreadsheet_url_or_id: spreadsheet || null,
        ...(keyJson.trim() ? { service_account_json: keyJson.trim() } : {}),
      },
      {
        onSuccess: () => {
          toast.push('Настройки Sheets сохранены', 'success');
          setKeyJson('');
          if (fileRef.current) fileRef.current.value = '';
        },
        onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось сохранить'),
      },
    );
  }

  function onFilePicked(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    file.text().then(setKeyJson).catch(() => toast.push('Не удалось прочитать файл'));
  }

  if (isLoading || !data) return <Skeleton className="h-24 w-full" />;
  const settings = data.settings;

  return (
    <div className="space-y-4">
      <div className="flex max-w-lg gap-2 rounded border border-border p-3">
        <div className="flex-1 space-y-1">
          <div className="text-sm font-medium">Сервисный аккаунт (рекомендуется)</div>
          <p className="text-xs text-muted-fg">Загрузите JSON-ключ и дайте этому адресу доступ на редактирование вашей таблицы.</p>
        </div>
        <Button type="button" variant="secondary" size="sm" disabled title="Подключим по вашему решению">
          OAuth Google
        </Button>
      </div>

      <form onSubmit={submit} className="max-w-md space-y-3 rounded border border-border p-3">
        <div>
          <label className="mb-1 block text-xs text-muted-fg" htmlFor="sheets-key-file">
            JSON-ключ сервисного аккаунта
          </label>
          <input ref={fileRef} id="sheets-key-file" type="file" accept="application/json" onChange={onFilePicked} className="text-xs" />
          {settings.service_account_email && (
            <p className="mt-1 text-xs text-muted-fg">
              Текущий: <span className="font-mono">{settings.service_account_email}</span> — дайте доступ на редактирование этой таблице этому адресу.
            </p>
          )}
        </div>
        <div>
          <label className="mb-1 block text-xs text-muted-fg" htmlFor="sheets-id">
            ID или ссылка на таблицу
          </label>
          <Input
            id="sheets-id"
            placeholder="https://docs.google.com/spreadsheets/d/…"
            value={spreadsheet}
            onChange={(e) => setSpreadsheet(e.target.value)}
          />
        </div>
        <div className="flex items-center gap-2">
          <Button type="submit" disabled={patch.isPending}>
            Сохранить
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!settings.service_account_key_set || !settings.spreadsheet_id || testAccess.isPending}
            onClick={() =>
              testAccess.mutate(undefined, {
                onSuccess: (res) => toast.push(`Доступ есть: «${res.title}»`, 'success'),
                onError: (err) => toast.push(err instanceof Error ? err.message : 'Нет доступа к таблице'),
              })
            }
          >
            Проверить доступ
          </Button>
          <Button
            type="button"
            variant="secondary"
            disabled={!settings.verified_at || testRow.isPending}
            onClick={() =>
              testRow.mutate(undefined, {
                onSuccess: (res) => toast.push(`Строка ${res.row} записана`, 'success'),
                onError: (err) => toast.push(err instanceof Error ? err.message : 'Не удалось записать строку'),
              })
            }
          >
            Записать тестовую строку
          </Button>
          {settings.verified_at && <Badge tone="success">Доступ подтверждён</Badge>}
        </div>
      </form>
    </div>
  );
}
